import APITemplate from '@/src/api/APITemplate'
import { diffTimeMinutes, formatQueryParams } from '@utils/Format'
import { toCsv } from '@utils/Array/toCsv/toCsv'
import { endpoint } from '@modules/tarefas/constants/URL'
import { TASK_DELETE_FORBIDDEN, TASK_DELETE_NOT_FOUND } from '@modules/tarefas/constants/Text'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import { TAREFA } from '@constants/Text'
import { GlobalBus } from '@services/bus'
import store from '@state/store'
// `con` — connection GLOBAL da plataforma (`@services/connection`, serializa
// FormData). Usado tanto nos endpoints REST do modulo (o backend de
// `aawz_project_management` faz dual-read: aceita FormData E JSON) quanto em
// TODA operacao de arquivo (anexos): upload (File cru no campo `file`, 1 request
// por arquivo, padrao aawz-drive / apiProfessionalAvatar) e download base64.
import con from '@services/connection'

/**
 * Adapter de tipos de campos personalizados (single source of truth para o alias options ⇄ select).
 * O backend persiste/retorna 'select'; o frontend usa 'options' como label/value.
 * Tipos não conhecidos passam direto (passthrough) preservando legado.
 */
const TYPE_FRONT_TO_BACK = { text: 'text', options: 'select', number: 'number', date: 'date' }
const TYPE_BACK_TO_FRONT = { text: 'text', select: 'options', number: 'number', date: 'date' }

// Tipos de contêiner de ACL (pasta/lista). O modal de permissões é único para
// os dois (contrato ui-components), então os métodos roteiam pela chave.
// As chaves são o contrato do backend (rotas `folderPermissions`/
// `sprintPermissions`); na UI e nas mensagens usamos sempre pasta/lista.
/**
 * Lê o payload da visão rápida nas DUAS formas do contrato.
 *
 * O backend responde `value` = array quando a requisição não pede paginação, e
 * `{items, total, page, pageSize}` quando pede (spec-002 F1, opt-in). As duas
 * convivem durante a migração; quando a F5 passar a sempre paginar, o ramo do
 * array sai daqui.
 *
 * Sem paginação, `total` é o próprio tamanho da lista — assim o consumidor não
 * precisa saber qual forma veio.
 */
// `totalsByBand` viaja DENTRO de `pagination` de propósito: é o mesmo pedaço da
// mesma resposta, e um slice novo no store custaria mutation + getter + action
// para carregar dado que nunca é lido separado da paginação.
//
// `{}` quando ausente (forma transitória sem paginação, ou backend anterior a
// esta versão) — o consumidor cai no total carregado, que é o comportamento
// antigo. Nunca `undefined`: o cabeçalho da faixa lê isso a cada render.
const EMPTY_BAND_TOTALS = {}

// Achata a resposta AGRUPADA `{groups:[{key,total,items}]}` no par
// `{tasks, pagination}` que o store já consome.
//
// O front continua agrupando em memória (`bucketTasksByCategory`) e chega nas
// MESMAS faixas — a paridade entre as duas regras é travada por par de testes.
// Por isso agrupar no servidor não exigiu trocar o store: o servidor passou a
// garantir COBERTURA por faixa, e o cliente segue dono da apresentação.
function _readQuickViewGroups(value) {
  const tasks = []
  const totalsByBand = {}
  let total = 0
  for (const group of value.groups) {
    if (!group || !group.key) continue
    totalsByBand[group.key] = group.total || 0
    total += group.total || 0
    for (const item of group.items || []) tasks.push(item)
  }
  return {
    tasks: tasks,
    pagination: {
      page: value.page || 1,
      pageSize: value.perBand || 0,
      total: total,
      totalsByBand: totalsByBand,
    },
  }
}

function _readQuickViewPayload(response) {
  const ok = response && response.status === 200
  const value = ok ? response.value : null
  if (value && Array.isArray(value.groups)) {
    return _readQuickViewGroups(value)
  }
  if (Array.isArray(value)) {
    return {
      tasks: value,
      pagination: {
        page: 1,
        pageSize: value.length,
        total: value.length,
        totalsByBand: EMPTY_BAND_TOTALS,
      },
    }
  }
  if (value && Array.isArray(value.items)) {
    return {
      tasks: value.items,
      pagination: {
        page: value.page || 1,
        pageSize: value.pageSize || value.items.length,
        total: value.total == null ? value.items.length : value.total,
        totalsByBand: value.totalsByBand || EMPTY_BAND_TOTALS,
      },
    }
  }
  return {
    tasks: [],
    pagination: {
      page: 1,
      pageSize: 0,
      total: 0,
      totalsByBand: EMPTY_BAND_TOTALS,
    },
  }
}

// Linhas-raiz que cada FAIXA traz por requisição no modo agrupado. Casa com a
// janela de render (`ROW_WINDOW_INITIAL`) de propósito: a primeira resposta já
// enche a janela de toda faixa, então nenhuma nasce com contador e zero linhas.
const QUICK_VIEW_PER_BAND = 30

/**
 * Monta os params de filtro/ordenação da visão rápida A PARTIR DO STORE.
 *
 * Ler o store aqui, em vez de receber os params do componente, é o que garante
 * que a página 2 use EXATAMENTE os mesmos filtros da página 1. Se cada call site
 * montasse os seus, um filtro alterado entre dois cliques faria a página seguinte
 * ser fatiada de outro conjunto — linhas repetidas e perdidas, sem erro visível.
 *
 * Também mantém os 3 call sites (`task-sidebar`, `view-task-board`,
 * `task-detail-modal`) inalterados, respeitando o C-1 (um único caminho de carga).
 */
function _quickViewFiltersFromStore() {
  const state = (store.state && store.state.tarefas) || {}
  const boardFilters = state.boardFilters || {}
  const dateFilter = state.dateFilter || {}
  const sortChain = Array.isArray(state.sortChain) ? state.sortChain : []
  // `undefined` e NÃO string vazia: `formatQueryParams` só omite `undefined`/`null`
  // (`utils/Format.js:1206`). Mandar `statusIds=` vazio faria o parser CSV do
  // backend receber string vazia onde espera lista — e a query ficaria poluída.
  const orEmpty = (value) => value || undefined
  return {
    // `campo:dir,campo:dir` — o backend descarta campo desconhecido.
    sort: orEmpty(
      sortChain
        .filter((entry) => entry && entry.field)
        .map((entry) => `${entry.field}:${entry.direction === 'desc' ? 'desc' : 'asc'}`)
        .join(',')
    ),
    search: orEmpty(state.searchQuery),
    statusIds: orEmpty(toCsv(state.statusFilter)),
    responsavelId: orEmpty(toCsv(boardFilters.responsavelIds)),
    clientId: orEmpty(toCsv(boardFilters.clientIds)),
    tagIds: orEmpty(toCsv(boardFilters.tagIds)),
    startDate: orEmpty(dateFilter.startDate),
    endDate: orEmpty(dateFilter.endDate),
  }
}

const CONTAINER_TYPE_FOLDER = 'folder'
const CONTAINER_TYPE_SPRINT = 'sprint'

// Mensagens dos guards de ACL. Extraídas porque se repetem entre métodos e
// nenhuma delas pode vazar identificador interno para o usuário.
const MSG_CONTAINER_TYPE_INVALID = 'Tipo de contêiner inválido. Selecione uma pasta ou lista novamente.'
const MSG_CONTAINER_ID_REQUIRED = 'Identificador do contêiner é obrigatório. Selecione a pasta ou lista novamente.'
const MSG_PERMISSION_ID_REQUIRED = 'Não foi possível identificar a permissão. Recarregue a lista e tente novamente.'
const MSG_TASK_ID_REQUIRED = 'Identificador da tarefa é obrigatório. Abra a tarefa novamente.'
const HTTP_BAD_REQUEST = 400

function mapTypeFrontToBack(type) {
  if (type == null) return type
  return TYPE_FRONT_TO_BACK[type] || type
}

function mapTypeBackToFront(type) {
  if (type == null) return type
  return TYPE_BACK_TO_FRONT[type] || type
}

// Percorre a árvore de tarefas (pais + `subtarefas` recursivas) e devolve as
// pastas distintas ali representadas. Usado para aquecer os status por pasta
// numa listagem cross-pasta (visão rápida), onde não há pasta ativa.
function _collectFolderIds(tasks) {
  const ids = new Set()
  const stack = Array.isArray(tasks) ? tasks.slice() : []
  while (stack.length) {
    const task = stack.pop()
    if (!task) continue
    if (task.folderId) ids.add(task.folderId)
    if (Array.isArray(task.subtarefas)) stack.push(...task.subtarefas)
  }
  return ids
}

// `date_new_ux` opera em DD/MM/YYYY mas emite a cada keystroke; este guard
// evita disparar o filtro com strings parciais como "01/05/2".
function _isCompleteDate(value) {
  if (!value) return false
  return /^\d{2}\/\d{2}\/\d{4}$/.test(String(value))
}

// Helpers para reduzir complexidade cognitiva de updateCustomField.
// `_isEmptyOptions`: trata `options` ausente/lista-vazia como remoção do campo
// no payload (evita 500 no reqparse). `_shouldSkipValue`: regra geral para
// pular chaves cujo valor é undefined/null (exceto `type`, que sempre passa
// pelo mapper).
function _isEmptyOptions(key, val) {
  if (key !== 'options') return false
  if (val === null) return true
  return Array.isArray(val) && val.length === 0
}

function _shouldSkipValue(key, val) {
  if (val === undefined) return true
  if (_isEmptyOptions(key, val)) return true
  if (key === 'type') return false
  return val === null
}

// O `connection.js` global serializa para FormData e só aplica
// `JSON.stringify` em valores com `.length` (arrays). Objetos puros de topo
// (sem `.length`), como `responsavelPrincipal { id, tipo }`, virariam a string
// "[object Object]" no wire. Para esses campos serializamos explicitamente em
// JSON-string antes do envio; o backend reidrata via parser tolerante. Retorna
// um clone raso do payload para não mutar o objeto original do chamador.
function _serializeResponsavelPrincipal(payload) {
  if (!payload || typeof payload !== 'object') return payload
  const responsavel = payload.responsavelPrincipal
  if (responsavel === undefined || responsavel === null) return payload
  return { ...payload, responsavelPrincipal: JSON.stringify(responsavel) }
}

function _buildCustomFieldUpdatePayload(data) {
  const payload = {}
  if (!data || typeof data !== 'object') return payload
  for (const key of Object.keys(data)) {
    const val = data[key]
    if (_shouldSkipValue(key, val)) continue
    payload[key] = key === 'type' ? mapTypeFrontToBack(val) : val
  }
  return payload
}

// Monta o dict de query params do GET /tasks. responsavelId/clientId/tagIds
// vão como CSV no wire — o backend (`_parse_int_csv`/`_parse_tag_ids`) aceita
// CSV e dispara OR-within-filter via `ANY(...)`. Datas parciais (digitação)
// são descartadas: o backend rejeitaria com 400. `includeCustomFields` e
// `includeClosed` são ADITIVOS: só entram na query quando truthy — sem os flags
// a URL é idêntica à atual (zero regressão para Kanban/Gantt e clientes antigos).
function _buildTasksQueryParams({
  sprintId,
  startDate,
  endDate,
  responsavelIds,
  clientIds,
  tagIds,
  includeCustomFields,
  includeClosed = false,
}) {
  const params = {}
  if (sprintId) params.sprintId = sprintId
  if (_isCompleteDate(startDate)) params.startDate = startDate
  if (_isCompleteDate(endDate)) params.endDate = endDate
  const csvResponsavel = toCsv(responsavelIds)
  if (csvResponsavel) params.responsavelId = csvResponsavel
  const csvClient = toCsv(clientIds)
  if (csvClient) params.clientId = csvClient
  const csvTags = toCsv(tagIds)
  if (csvTags) params.tagIds = csvTags
  if (includeCustomFields) params.includeCustomFields = true
  // Closed-status flag (AC-9, "Mostrar fechadas" toggle). Only sent when
  // truthy so the default request shape stays unchanged (backend defaults to
  // hiding closed tasks in list/kanban).
  if (includeClosed) params.includeClosed = true
  return params
}

class ApiTarefas extends APITemplate {
  constructor() {
    super()
    this.reqCarregar = null
    this.reqCarregarTags = null
    // Per-folder in-flight dedup for `carregarStatuses` — keyed by folderId
    // so concurrent loads of DIFFERENT folders don't block each other.
    this.reqCarregarStatuses = {}
  }

  getFolders() {
    return con.get(endpoint.folders)
  }

  /**
   * Cria uma pasta. Com `parentId` a pasta nasce como SUBPASTA (até 3 níveis).
   * A chave é OMITIDA do payload quando ausente: o connection global serializa
   * em FormData, então um `null` explícito chegaria ao backend como a string
   * "None" e seria tratado como id de mãe. `parentId` é escalar (string), logo
   * não precisa do `JSON.stringify` reservado a objeto-puro de topo.
   */
  createFolder({ name, parentId = null } = {}) {
    const payload = { name: name }
    if (parentId != null) payload.parentId = parentId
    return con.post(endpoint.folders, payload)
  }

  updateFolder({ id, data } = {}) {
    return con.put(endpoint.folderById(id), data)
  }

  deleteFolder({ id } = {}) {
    return con.delet(endpoint.folderById(id))
  }

  reorderFolders({ orderedIds } = {}) {
    return con.patch(endpoint.foldersReorder, { orderedIds: orderedIds })
  }

  /**
   * Move uma pasta para dentro de outra, ou a promove a pasta raiz.
   *
   * As subpastas, listas e tarefas acompanham pela FK — o backend altera UMA
   * única linha.
   *
   * ⚠️ `parentId` vai como STRING VAZIA quando é raiz, nunca como `null`:
   * `connection.js` faz `formData.append(key, value)` sem guarda de `null`, e o
   * `null` do JavaScript seria coagido para a string `"null"` no wire. O backend
   * aceita as duas formas, mas mandar `''` é o caminho explícito.
   *
   * O recarregamento acontece AQUI, na camada de API — mesmo padrão de
   * `moveTasks`. Necessário porque a mutation `UPDATE_FOLDER` faz merge NO
   * LUGAR e não reposiciona o nó na árvore: sem o reload, a pasta continuaria
   * aparecendo sob a mãe antiga até um F5.
   *
   * ⚠️ O reload é `store.dispatch('tarefas/loadFolders')`, NUNCA
   * `this.getFolders()`. Aquele último é um GET puro: devolve a resposta e não
   * toca no store. Chamá-lo aqui fazia a requisição sair, o backend responder
   * 200 e a árvore na tela continuar velha — bug pego em teste manual, porque
   * nenhum teste unitário observava o store.
   *
   * @param {Object} params
   * @param {String} params.folderId - UUID da pasta a mover
   * @param {String|null} params.parentId - UUID do destino; vazio/null = raiz
   * @returns {Promise<{status: Number, message: String, value: Object|null}>}
   */
  async moveFolder({ folderId, parentId } = {}) {
    if (!folderId) {
      return {
        status: 400,
        message: 'Não foi possível mover a pasta. Recarregue a página e tente novamente.',
        value: null,
      }
    }

    const r = await con.patch(endpoint.folderMove(folderId), {
      parentId: parentId || '',
    })

    if (r && r.status === 200) store.dispatch('tarefas/loadFolders')

    return (
      r || {
        status: 500,
        message: 'Não foi possível mover a pasta. Tente novamente mais tarde ou contate o suporte.',
        value: null,
      }
    )
  }

  // A pasta fica aqui; a LISTA (sprint) e o Modelo de Lista vivem em
  // `api/apiSprints.js` — recurso distinto de tarefa, camada de API própria.

  // ============================================================
  // Permissões de pasta, lista e tarefa (permissoes-pasta-lista).
  // ACL de contêiner (pasta/lista) + membros de tarefa privada.
  // Cada método valida o parâmetro obrigatório ANTES de montar a URL
  // (regra de segurança do CLAUDE.md — sem id não dispara request) e a
  // mensagem de erro sempre indica a próxima ação. Contrato: HTTP 200
  // sempre, status real no body; envelope { status, message, value }.
  // ============================================================

  /**
   * Lista a ACL de um contêiner (pasta ou lista) + estado + nível efetivo do
   * caller. Roteia por `targetType` — o modal de permissões é único para os dois.
   * @param {object} params
   * @param {'folder'|'sprint'} params.targetType - tipo do contêiner
   * @param {string} params.targetId - UUID do contêiner
   */
  getContainerPermissions({ targetType, targetId } = {}) {
    if (targetType !== CONTAINER_TYPE_FOLDER && targetType !== CONTAINER_TYPE_SPRINT) {
      return this.buildErrorResponse({ message: MSG_CONTAINER_TYPE_INVALID, status: HTTP_BAD_REQUEST, value: null })
    }
    if (!targetId) {
      return this.buildErrorResponse({ message: MSG_CONTAINER_ID_REQUIRED, status: HTTP_BAD_REQUEST, value: null })
    }
    const url = targetType === CONTAINER_TYPE_FOLDER
      ? endpoint.folderPermissions(targetId)
      : endpoint.sprintPermissions(targetId)
    return con.get(url)
  }

  /**
   * Adiciona uma entrada de ACL a um contêiner (pasta ou lista). Ao criar a
   * primeira entrada, o backend semeia o autor como `admin` (RQ-32).
   * @param {object} params
   * @param {'folder'|'sprint'} params.targetType - tipo do contêiner
   * @param {string} params.targetId - UUID do contêiner
   * @param {'equipe'|'profissional'} params.tipoSujeito - tipo do sujeito
   * @param {number} params.idSujeito - id da equipe ou profissional
   * @param {'view'|'edit'|'admin'} params.nivel - nível concedido
   */
  addContainerPermission({ targetType, targetId, tipoSujeito, idSujeito, nivel } = {}) {
    if (targetType !== CONTAINER_TYPE_FOLDER && targetType !== CONTAINER_TYPE_SPRINT) {
      return this.buildErrorResponse({ message: MSG_CONTAINER_TYPE_INVALID, status: HTTP_BAD_REQUEST, value: null })
    }
    if (!targetId) {
      return this.buildErrorResponse({ message: MSG_CONTAINER_ID_REQUIRED, status: HTTP_BAD_REQUEST, value: null })
    }
    if (!tipoSujeito || !idSujeito) {
      return this.buildErrorResponse({
        message: 'Selecione uma equipe ou profissional antes de adicionar a permissão.',
        status: HTTP_BAD_REQUEST,
        value: null,
      })
    }
    if (!nivel) {
      return this.buildErrorResponse({
        message: 'Selecione o nível de acesso (visualizar, editar ou administrar).',
        status: HTTP_BAD_REQUEST,
        value: null,
      })
    }
    const url = targetType === CONTAINER_TYPE_FOLDER
      ? endpoint.folderPermissions(targetId)
      : endpoint.sprintPermissions(targetId)
    return con.post(url, { tipoSujeito: tipoSujeito, idSujeito: idSujeito, nivel: nivel })
  }

  /**
   * Altera o nível de uma entrada de ACL existente.
   * @param {object} params
   * @param {string} params.permissionId - UUID da entrada
   * @param {'view'|'edit'|'admin'} params.nivel - novo nível
   */
  updatePermissionLevel({ permissionId, nivel } = {}) {
    if (!permissionId) {
      return this.buildErrorResponse({ message: MSG_PERMISSION_ID_REQUIRED, status: HTTP_BAD_REQUEST, value: null })
    }
    if (!nivel) {
      return this.buildErrorResponse({
        message: 'Selecione o novo nível de acesso.',
        status: HTTP_BAD_REQUEST,
        value: null,
      })
    }
    return con.put(endpoint.permissionById(permissionId), { nivel: nivel })
  }

  /**
   * Remove uma entrada de ACL. Remover a última entrada reabre a pasta (ou faz
   * a lista voltar a herdar) — a resposta traz `restrito: false` (RQ-26).
   * @param {object} params
   * @param {string} params.permissionId - UUID da entrada
   */
  removePermission({ permissionId } = {}) {
    if (!permissionId) {
      return this.buildErrorResponse({ message: MSG_PERMISSION_ID_REQUIRED, status: HTTP_BAD_REQUEST, value: null })
    }
    return con.delet(endpoint.permissionById(permissionId))
  }

  /**
   * Lista os membros de uma tarefa privada. Lista vazia = tarefa NÃO é privada.
   * @param {object} params
   * @param {string} params.taskId - UUID da tarefa
   */
  getTaskMembers({ taskId } = {}) {
    if (!taskId) {
      return this.buildErrorResponse({ message: MSG_TASK_ID_REQUIRED, status: HTTP_BAD_REQUEST, value: null })
    }
    return con.get(endpoint.taskMembers(taskId))
  }

  /**
   * Define a lista COMPLETA de membros de uma tarefa (idempotente). Lista vazia
   * despriva a tarefa (RQ-27) — é válido, por isso não é bloqueada pelo guard.
   * @param {object} params
   * @param {string} params.taskId - UUID da tarefa
   * @param {number[]} [params.idsProfissionais=[]] - ids finais dos membros
   */
  setTaskMembers({ taskId, idsProfissionais = [] } = {}) {
    if (!taskId) {
      return this.buildErrorResponse({ message: MSG_TASK_ID_REQUIRED, status: HTTP_BAD_REQUEST, value: null })
    }
    return con.put(endpoint.taskMembers(taskId), { idsProfissionais: idsProfissionais })
  }

  /**
   * Remove um membro de uma tarefa privada. Remover o último membro devolve a
   * tarefa ao contêiner efetivo (RQ-27).
   * @param {object} params
   * @param {string} params.taskId - UUID da tarefa
   * @param {number} params.profissionalId - id do profissional a remover
   */
  removeTaskMember({ taskId, profissionalId } = {}) {
    if (!taskId || !profissionalId) {
      return this.buildErrorResponse({
        message: 'Identificador da tarefa e do membro são obrigatórios para remover o acesso.',
        status: HTTP_BAD_REQUEST,
        value: null,
      })
    }
    return con.delet(endpoint.taskMemberById(taskId, profissionalId))
  }

  getTasks({
    sprintId = null,
    startDate = null,
    endDate = null,
    responsavelIds = null,
    clientIds = null,
    tagIds = null,
    includeCustomFields = false,
    includeClosed = false,
  } = {}) {
    const params = _buildTasksQueryParams({
      sprintId,
      startDate,
      endDate,
      responsavelIds,
      clientIds,
      tagIds,
      includeCustomFields,
      includeClosed,
    })
    return con.get(endpoint.tasks + formatQueryParams(params))
  }

  async getTaskById({ id } = {}) {
    // Valida o parâmetro obrigatório ANTES de montar a URL (sem `id`, viraria
    // `/tasks/undefined` chegando ao backend) e normaliza o retorno no shape
    // padrão `{ status, message, value }`, como os métodos irmãos da classe.
    if (!id) {
      return this.buildErrorResponse({
        message: 'id da tarefa é obrigatório.',
        value: null,
        status: 400,
      })
    }
    const r = await con.get(endpoint.taskById(id))
    return r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
  }

  // Visões rápidas — listagem cross-pasta filtrada por `view`. A resposta
  // tem o MESMO shape de getTasks (árvore de tarefas-pai com subtarefas),
  // então a list-view consome sem adaptação.
  async getQuickViewTasks({
    view,
    page,
    pageSize,
    perBand,
    band,
    sort,
    search,
    statusIds,
    responsavelId,
    clientId,
    tagIds,
    startDate,
    endDate,
  } = {}) {
    // `formatQueryParams` já omite vazio/nulo, então mandar só `view` continua
    // produzindo exatamente a query de antes — é isso que mantém a paginação
    // OPT-IN enquanto a F5 não liga o botão.
    const params = {
      view: view,
      page: page,
      perBand: perBand,
      band: band,
      pageSize: pageSize,
      sort: sort,
      search: search,
      statusIds: statusIds,
      responsavelId: responsavelId,
      clientId: clientId,
      tagIds: tagIds,
      startDate: startDate,
      endDate: endDate,
    }
    const r = await con.get(endpoint.quickViewTasks + formatQueryParams(params))
    return r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
  }

  // Visões rápidas — contadores { mine, today, next7, overdue, noDue }.
  async getQuickViewCounts() {
    const r = await con.get(endpoint.quickViewCounts)
    return r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
  }

  /**
   * Carrega a lista cross-pasta de uma visão rápida e popula o store.
   * Mesmo padrão de `carregarTags`/`carregarAssignees`: a chamada de rede vive
   * na camada de API (não numa action do Vuex) e o resultado é despachado via
   * setters do store. Componentes/orquestradores chamam este método direto.
   */
  async carregarQuickViewTasks({ view, page = 1, append = false, band = null } = {}) {
    store.dispatch('tarefas/setLoading', true)
    try {
      const r = await this.getQuickViewTasks({
        view: view,
        page: page,
        // Modo AGRUPADO: o servidor corta POR FAIXA. A paginação global por
        // linha-raiz (`pageSize`) era incorreta para lista agrupada — a página
        // é uma fatia de uma ordem GLOBAL e as faixas são uma partição do mesmo
        // conjunto, então nada garantia cobertura por faixa. Medido com
        // `sort=dueDate:asc`: a página 1 vinha sem NENHUMA linha de "A fazer" e
        // "Em andamento", que exibiam contador e lista vazia.
        perBand: QUICK_VIEW_PER_BAND,
        // Restringe a UMA faixa — é o "carregar mais" daquela faixa.
        band: band || undefined,
        ..._quickViewFiltersFromStore(),
      })
      const { tasks, pagination } = _readQuickViewPayload(r)
      // `append` é para a página 2+ do "Carregar mais": substituir zeraria o que
      // já está na tela. A F5 é quem passa a usá-lo.
      store.dispatch(append ? 'tarefas/appendTasks' : 'tarefas/setTasks', tasks)
      store.dispatch('tarefas/setQuickViewPagination', pagination)
      this._warmStatusesForTasks(tasks)
      return r
    } finally {
      store.dispatch('tarefas/setLoading', false)
    }
  }

  /**
   * Aquece o slice de status das pastas presentes numa lista cross-pasta.
   * A visão rápida não tem pasta ativa, então nenhum `loadFolderStatuses` é
   * disparado pela seleção — sem isso o dropdown de status da linha abre vazio
   * e a tarefa fica impossível de mover. Fire-and-forget: `carregarStatuses`
   * já é TTL-cached e deduplicado por pasta, então re-chamadas são baratas.
   */
  _warmStatusesForTasks(tasks) {
    for (const folderId of _collectFolderIds(tasks)) {
      this.carregarStatuses({ folderId: folderId })
    }
  }

  /**
   * Carrega os contadores das visões rápidas e popula o store (mesmo padrão
   * acima). Em falha, zera os contadores para manter a estrutura consistente.
   */
  async carregarQuickViewCounts() {
    const r = await this.getQuickViewCounts()
    store.dispatch('tarefas/setQuickViewCounts', r && r.status === 200 ? r.value || {} : {})
    return r
  }

  // Resolve short-link prefixes (8-char UUID truncations vindos do PATH estilo
  // ClickUp) de volta para UUIDs completos. Aceita folderShort/sprintShort/
  // taskShort (todos opcionais → query args l/s/t). Resolução hierárquica no
  // backend (sprint dentro do folder, task dentro do folder/sprint), respeitando
  // visibilidade. Retorna { status, message, value: { folderId, sprintId, taskId } }.
  // Guard: se as 3 forem falsy, não dispara request (nada a resolver).
  async resolveShortLink({ folderShort, sprintShort, taskShort } = {}) {
    if (!folderShort && !sprintShort && !taskShort) {
      return this.buildSuccessResponse({
        message: 'OK',
        value: { folderId: null, sprintId: null, taskId: null },
      })
    }
    const params = {}
    if (folderShort) params.l = folderShort
    if (sprintShort) params.s = sprintShort
    if (taskShort) params.t = taskShort
    const r = await con.get(endpoint.resolve + formatQueryParams(params))
    return r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
  }

  createTask({ task } = {}) {
    return con.post(endpoint.tasks, _serializeResponsavelPrincipal(task))
  }

  updateTask({ id, data } = {}) {
    return con.put(endpoint.taskById(id), _serializeResponsavelPrincipal(data))
  }

  /**
   * Delete de UMA tarefa. Fachada fina sobre `deleteTasks` — ha um unico
   * caminho de rede (`DELETE /tasks/batch`), entao ACL, purga de anexos S3 e
   * `task_deleted` no history sao exatamente os mesmos do lote.
   *
   * A traducao do resultado NAO e cosmetica: o lote responde `status:200` com
   * `count:0` + `skipped` quando a tarefa nao pode ser removida (invisivel ou
   * sem permissao), enquanto os chamadores individuais (`task-item`,
   * `task-detail-form`, `task-inline-subtask-row`) decidem sucesso por
   * `status === 200`. Sem converter o `skipped` de volta em 404/403, tentar
   * apagar uma tarefa sem permissao mostraria snack VERDE e o modal ainda
   * emitiria `task-deleted`, fechando a tela.
   *
   * @param {Object} params
   * @param {String} params.id - UUID da tarefa
   * @returns {Promise<{status: Number, message: String, value: Object|null}>}
   */
  async deleteTask({ id } = {}) {
    const r = await this.deleteTasks({ taskIds: [id] })
    if (!r || r.status !== 200) return r

    const value = r.value || {}
    if ((value.count || 0) > 0) return r

    const skipped = (value.skipped || [])[0] || {}
    const forbidden = skipped.reason === 'forbidden'
    return {
      status: forbidden ? 403 : 404,
      message: forbidden ? TASK_DELETE_FORBIDDEN : TASK_DELETE_NOT_FOUND,
      value: null,
    }
  }

  /**
   * Delete em lote (T006, US-1) — substitui o loop de N `DELETE /tasks/<id>`
   * (`task-batch-edit-row.vue`) por UMA requisicao. `con.delet` envia o corpo
   * no `config.data` (ver `services/connection.js:montarArgumentos`), entao o
   * array de ids trafega no corpo do DELETE, nao na query string.
   *
   * A chamada de rede E a limpeza do store vivem aqui — mesmo padrao de
   * `carregarQuickViewTasks`: componentes chamam este metodo direto e o
   * resultado e despachado via setter (`tarefas/removeTasksFromStore`). O
   * guard de volume continua no componente (T008).
   *
   * @param {Object} params
   * @param {Array<String>} params.taskIds - UUIDs das tarefas a deletar
   * @returns {Promise<{status: Number, message: String, value: Object|null}>}
   *   `value` no formato `{deletedTaskIds, count, requested, skipped}`
   */
  async deleteTasks({ taskIds } = {}) {
    const r = await con.delet(endpoint.tasksBatch, { taskIds: taskIds })
    if (r && r.status === 200) {
      // Limpa a tela com os ids que o SERVIDOR confirma ter deletado
      // (`value.deletedTaskIds`), NUNCA com os `taskIds` pedidos: em sucesso
      // parcial os dois divergem, e usar o pedido apagaria da arvore uma
      // tarefa que continua existindo no backend.
      store.dispatch('tarefas/removeTasksFromStore', {
        taskIds: (r.value && r.value.deletedTaskIds) || [],
      })
    }
    return r
  }

  reorderTasks({ sprintId, status, orderedIds } = {}) {
    return con.patch(endpoint.tasksReorder, { sprintId: sprintId, status: status, orderedIds: orderedIds })
  }

  // ============================================================
  // List preferences (pm_user_list_preference) — preferência PESSOAL
  // de colunas da List view, escopada por (usuário, sprint). Contrato:
  // `columns` = array ordenado de chaves visíveis (built-ins ou
  // `cf:<uuid>`); chaves fixas (`title`/`actions`) nunca são enviadas.
  // Respostas sempre HTTP 200 com status real no body.
  // ============================================================

  /**
   * Reads the saved column preference for a sprint.
   * `value: null` = sem preferência salva (frontend aplica o default);
   * `status: 404` = sprint inexistente/de outro escritório.
   * @param {object} params
   * @param {string} params.sprintId - sprint UUID
   */
  getListPreference({ sprintId } = {}) {
    return con.get(endpoint.listPreferences + formatQueryParams({ sprintId: sprintId }))
  }

  /**
   * Upserts the column preference for a sprint. Backend valida whitelist,
   * duplicatas e máx. 30 colunas (status 400 no body) e devolve o estado
   * salvo pós lazy-cleanup de custom fields deletados.
   * @param {object} params
   * @param {string} params.sprintId - sprint UUID
   * @param {string[]} params.columns - chaves visíveis, em ordem
   */
  saveListPreference({ sprintId, columns } = {}) {
    return con.put(endpoint.listPreferences, { sprintId: sprintId, columns: columns })
  }

  /**
   * Deletes the column preference for a sprint (restaura o default).
   * Idempotente: deletar preferência inexistente também retorna 200.
   * @param {object} params
   * @param {string} params.sprintId - sprint UUID
   */
  deleteListPreference({ sprintId } = {}) {
    return con.delet(endpoint.listPreferences + formatQueryParams({ sprintId: sprintId }))
  }

  // True quando é seguro commitar o slice da preferência para esta sprint —
  // evita que respostas tardias de uma sprint anterior clobberem o estado da
  // sprint atual (race na troca rápida de sprint + flush de save pendente).
  // Lê `activeSprintId` fresco do store a cada chamada.
  // eslint-disable-next-line class-methods-use-this
  _listPreferenceSprintActive(sprintId) {
    const activeId = store.state.tarefas ? store.state.tarefas.activeSprintId : null
    return activeId == null || activeId === sprintId
  }

  /**
   * Carrega a preferência salva para a sprint e popula o store. Mesmo padrão de
   * `carregarQuickViewTasks`/`carregarTags`: a chamada de rede vive na camada de
   * API (não numa action do Vuex) e o resultado é despachado via setter puro
   * `tarefas/setListPreference`. `value: null` (sem registro) mantém
   * `columns: null` → default. Stale guard: se o usuário trocou de sprint
   * enquanto o fetch estava em voo, a resposta antiga é descartada para não
   * sobrescrever a preferência da sprint atual.
   */
  async carregarListPreference({ sprintId } = {}) {
    if (!sprintId) return null
    store.dispatch('tarefas/setListPreference', { sprintId: sprintId, columns: null, loading: true })
    const r = await this.getListPreference({ sprintId: sprintId })
    if (!this._listPreferenceSprintActive(sprintId)) {
      return r
    }
    const value = r && r.status === 200 ? r.value : null
    store.dispatch('tarefas/setListPreference', {
      sprintId: sprintId,
      columns: value && Array.isArray(value.columns) ? value.columns : null,
      loading: false,
    })
    return r
  }

  /**
   * Upsert otimista: aplica `columns` na UI imediatamente, faz o PUT em
   * background e, em erro (status != 200), faz rollback para o snapshot
   * anterior. Em sucesso, commita o estado salvo retornado pelo backend
   * (pós lazy-cleanup de custom fields deletados). Retorna a resposta para
   * o caller exibir feedback (`$showSnack(r)`).
   *
   * Stale guard: se a sprint ativa mudou (antes do PUT ou enquanto ele estava
   * em voo), o PUT ainda persiste a preferência da sprint antiga, mas NENHUM
   * dispatch toca o slice — o estado pertence à sprint atual.
   */
  async persistListPreference({ sprintId, columns } = {}) {
    const current =
      (store.state.tarefas && store.state.tarefas.listPreference) || { sprintId: null, columns: null }
    const previous = { sprintId: current.sprintId, columns: current.columns, loading: false }
    if (this._listPreferenceSprintActive(sprintId)) {
      store.dispatch('tarefas/setListPreference', { sprintId: sprintId, columns: columns, loading: true })
    }
    const r = await this.saveListPreference({ sprintId: sprintId, columns: columns })
    if (!this._listPreferenceSprintActive(sprintId)) {
      return r
    }
    if (r && r.status === 200) {
      const value = r.value || {}
      store.dispatch('tarefas/setListPreference', {
        sprintId: value.sprintId || sprintId,
        columns: Array.isArray(value.columns) ? value.columns : columns,
        loading: false,
      })
    } else {
      store.dispatch('tarefas/setListPreference', previous)
    }
    return r
  }

  /**
   * "Restaurar padrão": DELETE otimista (UI volta ao default imediatamente);
   * rollback em erro. O DELETE é idempotente no backend. Mesmo stale guard de
   * `persistListPreference` — resposta tardia de sprint anterior não commita.
   */
  async restaurarListPreference({ sprintId } = {}) {
    const current =
      (store.state.tarefas && store.state.tarefas.listPreference) || { sprintId: null, columns: null }
    const previous = { sprintId: current.sprintId, columns: current.columns, loading: false }
    if (this._listPreferenceSprintActive(sprintId)) {
      store.dispatch('tarefas/setListPreference', { sprintId: sprintId, columns: null, loading: true })
    }
    const r = await this.deleteListPreference({ sprintId: sprintId })
    if (!this._listPreferenceSprintActive(sprintId)) {
      return r
    }
    if (r && r.status === 200) {
      store.dispatch('tarefas/setListPreference', { sprintId: sprintId, columns: null, loading: false })
    } else {
      store.dispatch('tarefas/setListPreference', previous)
    }
    return r
  }

  /**
   * Moves tasks to another list (folder + sprint) in ONE atomic backend call
   * and applies refresh-after-mutation. `taskIds` are PARENT task ids only —
   * the backend resolves and moves subtasks recursively. Payload is camelCase
   * (JSON via connectionJson).
   *
   * Following the module convention (same as `carregarQuickViewTasks`), the
   * network call AND the post-success store refresh live in the API layer —
   * components call this method directly instead of going through a Vuex
   * action. On success: reloads the active sprint's tasks (a task moved OUT
   * of the active sprint disappears from the board — correct), refreshes the
   * sidebar folders and the overview stats. On any non-200 we do NOT refresh;
   * the caller reads `r.status`/`r.message` to surface the error.
   * @param {object} params
   * @param {string[]} params.taskIds - parent task UUIDs to move
   * @param {string} params.folderId - destination folder UUID
   * @param {string} params.sprintId - destination sprint UUID
   */
  async moveTasks({ taskIds, folderId, sprintId } = {}) {
    const r = await con.patch(endpoint.tasksMove, { taskIds: taskIds, folderId: folderId, sprintId: sprintId })
    if (r && r.status === 200) {
      const activeSprintId = store.state.tarefas ? store.state.tarefas.activeSprintId : null
      store.dispatch('tarefas/loadTasks', { sprintId: activeSprintId })
      store.dispatch('tarefas/loadFolders')
      store.dispatch('tarefas/loadOverviewStats')
    }
    return r
  }

  getComments({ taskId } = {}) {
    return con.get(endpoint.taskComments(taskId))
  }

  // Sobe `files` 1-a-1 via connection global (padrao da plataforma: File cru no
  // campo `file`, multipart implicito do connection). Agrega os anexos criados.
  // Para no 1o erro e devolve o status do erro junto com os ja enviados.
  async _uploadAttachments(url, files) {
    const uploaded = []
    for (const file of files) {
      if (!file) continue
      const r = await con.post(url, { file: file, name: file.name })
      if (!r || r.status !== 200) {
        return {
          status: (r && r.status) || 500,
          message: (r && r.message) || 'Erro ao enviar arquivo.',
          value: uploaded,
        }
      }
      if (Array.isArray(r.value)) uploaded.push(...r.value)
      else if (r.value) uploaded.push(r.value)
    }
    return { status: 200, message: 'OK', value: uploaded }
  }

  // Cria o comentario (JSON) e, havendo arquivos, sobe cada um como anexo via
  // connection global (1 request por arquivo). Os anexos enviados sao anexados
  // ao comentario retornado para o store refletir texto + anexos de uma vez.
  async addComment({ taskId, text, files = null } = {}) {
    const r = await con.post(endpoint.taskComments(taskId), { text: text })
    if (!files || !files.length) return r
    if (!r || r.status !== 200 || !r.value) return r
    const up = await this._uploadAttachments(
      endpoint.commentAttachments(r.value.id),
      files
    )
    if (up.value && up.value.length) {
      r.value.attachments = (r.value.attachments || []).concat(up.value)
    }
    if (up.status !== 200) {
      return { status: up.status, message: up.message, value: r.value }
    }
    return r
  }

  updateComment({ commentId, text } = {}) {
    return con.put(endpoint.commentById(commentId), { text: text })
  }

  deleteComment({ commentId } = {}) {
    return con.delet(endpoint.commentById(commentId))
  }

  deleteCommentAttachment({ commentId, attachmentId } = {}) {
    if (!commentId || !attachmentId) {
      return Promise.resolve({ status: 400, message: 'Anexo inválido.', value: null })
    }
    return con.delet(endpoint.commentAttachmentById(commentId, attachmentId))
  }

  // Baixa o anexo de comentario em base64 (contrato downloadBase64 da
  // plataforma) via connection global. GET -> { status, value: { base64, name,
  // extension } }. Usado tanto para download real quanto para refrescar a
  // exibicao de imagem cuja presigned inline expirou (montando um data URI).
  downloadCommentAttachment({ commentId, attachmentId } = {}) {
    if (!commentId || !attachmentId) {
      return Promise.resolve({ status: 400, message: 'Anexo inválido.', value: null })
    }
    return con.get(endpoint.commentAttachmentDownload(commentId, attachmentId))
  }

  // ---------------------------------------------------------------------
  // Anexos diretamente na TAREFA (pm_task_attachment) — espelha os metodos
  // de anexo de comentario, trocando o vinculo commentId -> taskId. Lista,
  // append, delete e download base64. Tudo via connection global (`con`):
  // arquivo (append + download) vai como FormData; lista e delete idem.
  // ---------------------------------------------------------------------

  // Lista os anexos de uma tarefa (presigned inline). GET -> { status, value: [att...] }.
  getTaskAttachments({ taskId } = {}) {
    return con.get(endpoint.taskAttachments(taskId))
  }

  // Append de anexos a uma tarefa JA existente (edit-mode + retry de arquivo
  // que falhou). Sobe 1 request por arquivo -> { status, value: [attachment...] }.
  addTaskAttachments({ taskId, files = null } = {}) {
    if (!taskId) {
      return Promise.resolve({ status: 400, message: 'Tarefa inválida.', value: null })
    }
    if (!files || !files.length) {
      return Promise.resolve({ status: 400, message: 'Nenhum arquivo para anexar.', value: null })
    }
    return this._uploadAttachments(endpoint.taskAttachments(taskId), files)
  }

  deleteTaskAttachment({ taskId, attachmentId } = {}) {
    if (!taskId || !attachmentId) {
      return Promise.resolve({ status: 400, message: 'Anexo inválido.', value: null })
    }
    return con.delet(endpoint.taskAttachmentById(taskId, attachmentId))
  }

  // Baixa o anexo de tarefa em base64 (contrato downloadBase64 da plataforma)
  // via connection global. GET -> { status, value: { base64, name, extension } }.
  // Usado tanto para download real quanto para refrescar a exibicao de imagem
  // cuja presigned inline expirou (montando um data URI).
  downloadTaskAttachment({ taskId, attachmentId } = {}) {
    if (!taskId || !attachmentId) {
      return Promise.resolve({ status: 400, message: 'Anexo inválido.', value: null })
    }
    return con.get(endpoint.taskAttachmentDownload(taskId, attachmentId))
  }

  /**
   * Builds the optional `startDate`/`endDate`/`responsavelId`/`clientId`
   * filter params shared by all 5 overview endpoints. Empty/incomplete
   * dates are dropped on the wire so the backend never receives partial
   * strings (which would 400). Returns a fresh dict — callers add their
   * endpoint-specific params on top.
   */
  // eslint-disable-next-line class-methods-use-this
  _buildOverviewFilterParams({ startDate, endDate, responsavelIds, clientIds } = {}) {
    const params = {}
    if (_isCompleteDate(startDate)) params.startDate = startDate
    if (_isCompleteDate(endDate)) params.endDate = endDate
    const csvResponsavel = toCsv(responsavelIds)
    if (csvResponsavel) params.responsavelId = csvResponsavel
    const csvClient = toCsv(clientIds)
    if (csvClient) params.clientId = csvClient
    return params
  }

  getOverviewStats({ startDate, endDate, responsavelIds, clientIds } = {}) {
    const params = this._buildOverviewFilterParams({
      startDate, endDate, responsavelIds, clientIds,
    })
    return con.get(endpoint.overviewStats + formatQueryParams(params))
  }

  /**
   * Top-N parent tasks that are overdue (priority DESC, due_date ASC).
   * Backend pre-aggregates so the frontend never iterates `state.tasks`.
   * @param {object} [params]
   * @param {number} [params.limit=10] - 1..50
   */
  getOverviewOverdueTasks({
    limit = 10, startDate, endDate, responsavelIds, clientIds,
  } = {}) {
    const params = this._buildOverviewFilterParams({
      startDate, endDate, responsavelIds, clientIds,
    })
    params.limit = limit
    return con.get(endpoint.overviewOverdue + formatQueryParams(params))
  }

  /**
   * Top-N parent tasks due in the current ISO week (Monday-Sunday), excluding
   * done/closed. Window is computed server-side. AC-3 (left card).
   * @param {object} [params]
   * @param {number} [params.limit=10] - 1..50
   */
  getOverviewWeeklyDue({ limit = 10 } = {}) {
    return con.get(endpoint.overviewWeeklyDue + formatQueryParams({ limit: limit }))
  }

  /**
   * Top-N parent tasks completed (status='done') in the current ISO week,
   * ordered by most-recently updated. Window is computed server-side.
   * AC-3 (right card).
   * @param {object} [params]
   * @param {number} [params.limit=10] - 1..50
   */
  getOverviewWeeklyDone({ limit = 10 } = {}) {
    return con.get(endpoint.overviewWeeklyDone + formatQueryParams({ limit: limit }))
  }

  /**
   * Top-N member distribution (open parent tasks by first non-client assignee)
   * with synthetic "Outros" bucket aggregating the long tail.
   * @param {object} [params]
   * @param {number} [params.limit=10] - 1..50
   */
  getOverviewMemberDistribution({
    limit = 10, startDate, endDate, responsavelIds, clientIds,
  } = {}) {
    const params = this._buildOverviewFilterParams({
      startDate, endDate, responsavelIds, clientIds,
    })
    params.limit = limit
    return con.get(endpoint.overviewMembers + formatQueryParams(params))
  }

  /**
   * Top-N client distribution (open parent tasks by `id_cliente_app`) with
   * synthetic "Outros" bucket aggregating the long tail.
   * @param {object} [params]
   * @param {number} [params.limit=10] - 1..50
   */
  getOverviewClientDistribution({
    limit = 10, startDate, endDate, responsavelIds, clientIds,
  } = {}) {
    const params = this._buildOverviewFilterParams({
      startDate, endDate, responsavelIds, clientIds,
    })
    params.limit = limit
    return con.get(endpoint.overviewClients + formatQueryParams(params))
  }

  /**
   * Monthly created/resolved series for the bar chart, zero-filled per month.
   * `created` uses `data_registro`; `resolved` uses `data_atualizacao` for
   * `status='done'` (semantic fix vs the legacy frontend getter).
   * @param {object} [params]
   * @param {number} [params.months=12] - 1..24
   */
  getOverviewChartMonthly({
    months = 12, startDate, endDate, responsavelIds, clientIds,
  } = {}) {
    const params = this._buildOverviewFilterParams({
      startDate, endDate, responsavelIds, clientIds,
    })
    params.months = months
    return con.get(endpoint.overviewChartMonthly + formatQueryParams(params))
  }

  /**
   * Lists tags for the current office.
   * Default behavior returns only `active=true` tags (picker-friendly).
   * Pass `includeInactive=true` from the settings page to see archived tags.
   *
   * @param {object} [params]
   * @param {boolean} [params.includeInactive=false] - include archived tags
   */
  getTags({ includeInactive = false } = {}) {
    let url = endpoint.tags
    if (includeInactive) {
      url += formatQueryParams({ include_inactive: true })
    }
    return con.get(url)
  }

  /**
   * Creates a tag (gestor-only — backend enforces).
   *
   * @param {object} params
   * @param {string} params.name - tag name (required, max 100 chars)
   * @param {string} [params.color] - hex color (default backend `#808080`)
   * @param {boolean} [params.active=true] - default true
   */
  createTag({ name, color, active = true } = {}) {
    return con.post(endpoint.tags, { name: name, color: color, active: active })
  }

  /**
   * Updates a tag — partial body. Only includes keys explicitly provided.
   * Used for edit, archive (`active=false`), and restore (`active=true`).
   *
   * @param {object} params
   * @param {string} params.tagId - target tag uuid
   * @param {string} [params.name]
   * @param {string} [params.color]
   * @param {boolean} [params.active]
   */
  updateTag({ tagId, name, color, active } = {}) {
    const data = {}
    if (name !== undefined) data.name = name
    if (color !== undefined) data.color = color
    if (active !== undefined) data.active = active
    return con.put(endpoint.tagById(tagId), data)
  }

  /**
   * Archives a tag (`active=false`). Junction rows in `pm_task_tag` are NOT
   * cascaded — existing attached tags stay visible (dimmed in the UI).
   */
  archiveTag({ tagId } = {}) {
    return this.updateTag({ tagId: tagId, active: false })
  }

  /**
   * Restores a previously archived tag (`active=true`).
   */
  restoreTag({ tagId } = {}) {
    return this.updateTag({ tagId: tagId, active: true })
  }

  /**
   * Cached singleton fetch for the office tag list.
   *
   * Mirrors `carregarAssignees` (above): `this.reqCarregarTags` dedupes
   * concurrent in-flight requests; TTL via `MINUTES_TIMEOUT_REQUESTS` against
   * `tarefas/tagsLastUpdate`. On non-200 responses we preserve the last-good
   * cache (no reset). Dispatches `tarefas/setTags` on success.
   *
   * @param {object} [params]
   * @param {boolean} [params.forceUpdate=false] - bypass TTL
   * @param {boolean} [params.includeInactive=false] - settings page passes true
   */
  // eslint-disable-next-line max-statements
  async carregarTags({ forceUpdate = false, includeInactive = false } = {}) {
    if (this.reqCarregarTags != null) {
      return this.reqCarregarTags
    }
    if (!forceUpdate) {
      if (
        !diffTimeMinutes(
          store.getters['tarefas/tagsLastUpdate'],
          MINUTES_TIMEOUT_REQUESTS
        )
      ) {
        return store.getters['tarefas/tags']
      }
    }

    this.reqCarregarTags = this.getTags({ includeInactive: includeInactive })
    const r = await this.reqCarregarTags

    if (r && r.status === 200) {
      store.dispatch('tarefas/setTags', r.value || [])
    }

    this.reqCarregarTags = null

    return r || {}
  }

  // ============================================================
  // Per-folder custom statuses (pm_task_status) — gestor-only
  // mutations; read open to any professional (board must render
  // statuses for everyone). Two-layer scoping (id_escritorio +
  // id_folder) is enforced server-side; cross-folder/cross-office
  // returns 404 in the body (HTTP 200 always).
  // ============================================================

  /**
   * Lists a folder's statuses ordered by `position`.
   * @param {object} params
   * @param {string} params.folderId - owning folder UUID (required)
   * @param {boolean} [params.includeInactive=false] - include archived statuses
   */
  getFolderStatuses({ folderId, includeInactive = false } = {}) {
    if (!folderId) {
      return Promise.resolve({ status: 400, message: 'folderId obrigatório.', value: null })
    }
    let url = endpoint.folderStatuses(folderId)
    if (includeInactive) {
      url += formatQueryParams({ includeInactive: true })
    }
    return con.get(url)
  }

  /**
   * Creates a status in a folder (gestor-only — backend enforces).
   * `position` is auto-assigned server-side (max+1 within the folder).
   * @param {object} params
   * @param {string} params.folderId - owning folder UUID (required)
   * @param {object} params.data - camelCase payload { name, color, category, isDefault? }
   */
  createStatus({ folderId, data } = {}) {
    if (!folderId) {
      return Promise.resolve({ status: 400, message: 'folderId obrigatório.', value: null })
    }
    return con.post(endpoint.folderStatuses(folderId), data || {})
  }

  /**
   * Updates a status — partial body, only sent keys (name/color/category/
   * isDefault/active). Gestor-only.
   * @param {object} params
   * @param {string} params.id - status UUID (required)
   * @param {object} params.data - camelCase partial payload
   */
  updateStatus({ id, data } = {}) {
    if (!id) {
      return Promise.resolve({ status: 400, message: 'id obrigatório.', value: null })
    }
    return con.put(endpoint.statusById(id), data || {})
  }

  /**
   * Deletes a status (gestor-only). When the status is in use, the caller
   * MUST pass `reassignToId` (a status of the SAME folder) so the backend
   * moves affected tasks before removing the original. Without it, the
   * backend returns 400 with `value.tasksAffected`.
   * @param {object} params
   * @param {string} params.id - status UUID (required)
   * @param {string} [params.reassignToId] - target status UUID (same folder)
   */
  deleteStatus({ id, reassignToId } = {}) {
    if (!id) {
      return Promise.resolve({ status: 400, message: 'id obrigatório.', value: null })
    }
    let url = endpoint.statusById(id)
    if (reassignToId) {
      url += '?reassignToId=' + encodeURIComponent(reassignToId)
    }
    return con.delet(url)
  }

  /**
   * Reorders all statuses of a folder (gestor-only). Sends the COMPLETE
   * ordered id list — backend rewrites each `position` to its index.
   *
   * Usa POST (não PATCH): o CORS do app AAWZ não libera PATCH no preflight
   * cross-origin, então um PATCH cross-origin é bloqueado pelo browser e cai no
   * catch do connectionJson como "Erro de conexão". POST é a convenção da
   * plataforma para esse caso (mesmo motivo do edit manual de transcrições).
   * @param {object} params
   * @param {string} params.folderId - owning folder UUID (required)
   * @param {string[]} params.orderedIds - all status ids of the folder, in order
   */
  reorderStatuses({ folderId, orderedIds } = {}) {
    if (!folderId) {
      return Promise.resolve({ status: 400, message: 'folderId obrigatório.', value: null })
    }
    return con.post(endpoint.folderStatusesReorder(folderId), { orderedIds: orderedIds })
  }

  // ------------------------------------------------------------------
  // Persisting wrappers (request + store write) — mirror `carregarStatuses`.
  // The API layer owns the network call AND the Vuex commit (via pure setter
  // actions), so components call these directly and Vuex actions never fetch.
  // ------------------------------------------------------------------

  /**
   * Creates a status and, on success, writes it into the folder's slice.
   * @param {object} params
   * @param {string} params.folderId - owning folder UUID (required)
   * @param {object} params.data - camelCase payload { name, color, category, isDefault? }
   */
  async criarStatus({ folderId, data } = {}) {
    const r = await this.createStatus({ folderId: folderId, data: data })
    if (r && r.status === 200 && r.value) {
      store.dispatch('tarefas/addFolderStatusToStore', {
        folderId: folderId,
        status: r.value,
      })
    }
    return r
  }

  /**
   * Updates a status and, on success, merges it into the folder's slice.
   * @param {object} params
   * @param {string} params.folderId - owning folder UUID (required for the store write)
   * @param {string} params.id - status UUID (required)
   * @param {object} params.data - camelCase partial payload
   */
  async atualizarStatus({ folderId, id, data } = {}) {
    const r = await this.updateStatus({ id: id, data: data })
    if (r && r.status === 200 && r.value) {
      store.dispatch('tarefas/updateFolderStatusInStore', {
        folderId: folderId,
        status: r.value,
      })
    }
    return r
  }

  /**
   * Deletes a status and, on success, drops it from the folder's slice. When
   * the status is in use the backend returns 400 with `value.tasksAffected`
   * (no store write) so the caller can surface the reassign flow.
   * @param {object} params
   * @param {string} params.folderId - owning folder UUID (required for the store write)
   * @param {string} params.id - status UUID (required)
   * @param {string} [params.reassignToId] - target status UUID (same folder)
   */
  async removerStatus({ folderId, id, reassignToId } = {}) {
    const r = await this.deleteStatus({ id: id, reassignToId: reassignToId })
    if (r && r.status === 200) {
      store.dispatch('tarefas/removeFolderStatusFromStore', {
        folderId: folderId,
        statusId: id,
      })
    }
    return r
  }

  /**
   * Reorders a folder's statuses and syncs the slice on success. Prefers the
   * authoritative ordered list from the backend when present; otherwise applies
   * the local `orderedIds` reorder.
   * @param {object} params
   * @param {string} params.folderId - owning folder UUID (required)
   * @param {string[]} params.orderedIds - all status ids of the folder, in order
   */
  async reordenarStatuses({ folderId, orderedIds } = {}) {
    const r = await this.reorderStatuses({ folderId: folderId, orderedIds: orderedIds })
    if (r && r.status === 200) {
      if (Array.isArray(r.value)) {
        store.dispatch('tarefas/setFolderStatuses', {
          folderId: folderId,
          statuses: r.value,
        })
      } else {
        store.dispatch('tarefas/setFolderStatusesOrder', {
          folderId: folderId,
          orderedIds: orderedIds,
        })
      }
    }
    return r
  }

  /**
   * Cached, per-folder fetch of the status list.
   *
   * Mirrors `carregarTags`: dedupes concurrent in-flight requests PER FOLDER
   * (`this.reqCarregarStatuses[folderId]`); TTL via `MINUTES_TIMEOUT_REQUESTS`
   * against `tarefas/statusesLastUpdate[folderId]`. On 200 dispatches
   * `tarefas/setFolderStatuses`. On non-200 preserves the last-good cache.
   * Returns a deep-cloned cached value on the fresh path so consumers never
   * mutate the live Vuex array.
   *
   * @param {object} params
   * @param {string} params.folderId - owning folder UUID (required)
   * @param {boolean} [params.forceUpdate=false] - bypass TTL
   * @param {boolean} [params.includeInactive=false] - include archived statuses
   */
  // eslint-disable-next-line max-statements
  async carregarStatuses({ folderId, forceUpdate = false, includeInactive = false } = {}) {
    if (!folderId) {
      return { status: 400, message: 'folderId obrigatório.', value: null }
    }
    if (this.reqCarregarStatuses[folderId] != null) {
      return this.reqCarregarStatuses[folderId]
    }
    const lastUpdateMap = store.getters['tarefas/statusesLastUpdateMap'] || {}
    if (!forceUpdate) {
      if (!diffTimeMinutes(lastUpdateMap[folderId], MINUTES_TIMEOUT_REQUESTS)) {
        const cached = store.getters['tarefas/statusesForFolder'](folderId) || []
        // Deep clone so consumers can't mutate the live Vuex array.
        return { status: 200, value: structuredClone(cached) }
      }
    }

    store.dispatch('tarefas/setFolderStatusesLoading', { folderId: folderId, value: true })
    this.reqCarregarStatuses[folderId] = this.getFolderStatuses({
      folderId: folderId,
      includeInactive: includeInactive,
    })
    const r = await this.reqCarregarStatuses[folderId]

    if (r && r.status === 200) {
      store.dispatch('tarefas/setFolderStatuses', {
        folderId: folderId,
        statuses: r.value || [],
      })
    }

    store.dispatch('tarefas/setFolderStatusesLoading', { folderId: folderId, value: false })
    this.reqCarregarStatuses[folderId] = null

    return r || {}
  }

  addTagToTask({ taskId, tagId } = {}) {
    return con.post(endpoint.taskTags(taskId), { tagId: tagId })
  }

  removeTagFromTask({ taskId, tagId } = {}) {
    return con.delet(endpoint.taskTagById(taskId, tagId))
  }

  addAssignee({ taskId, profissionalId } = {}) {
    return con.post(endpoint.taskAssignees(taskId), { id_profissional: profissionalId })
  }

  removeAssignee({ taskId, profissionalId } = {}) {
    return con.delet(endpoint.taskAssigneeById(taskId, profissionalId))
  }

  getChecklists({ taskId } = {}) {
    return con.get(endpoint.taskChecklists(taskId))
  }

  createChecklist({ taskId, name } = {}) {
    return con.post(endpoint.taskChecklists(taskId), { name: name })
  }

  createChecklistItem({ checklistId, description } = {}) {
    return con.post(endpoint.checklistItems(checklistId), { description: description })
  }

  updateChecklistItem({ itemId, data } = {}) {
    return con.put(endpoint.checklistItemById(itemId), data)
  }

  deleteChecklistItem({ itemId } = {}) {
    return con.delet(endpoint.checklistItemById(itemId))
  }

  reorderChecklistItems({ checklistId, orderedIds } = {}) {
    return con.patch(endpoint.checklistItemsReorder(checklistId), { orderedIds: orderedIds })
  }

  /**
   * Fetches available assignees (professionals + clients) for the current office.
   * Clients are scoped by role: gestores see all, professionals see only linked clients.
   * Filtering is client-side; no query parameter is sent to the backend.
   */
  getAssignees() {
    return con.get(endpoint.assignees)
  }

  /**
   * Cached singleton fetch for the assignees list.
   *
   * Mirrors the platform-wide pattern at `aawz-web-frontend/jsTemplates/api.js`
   * and the production reference `apiProfessionals.carregar()`. Behavior:
   *   - `this.reqCarregar` dedupes concurrent in-flight requests.
   *   - TTL via `MINUTES_TIMEOUT_REQUESTS` against `tarefas/assigneesLastUpdate`.
   *   - Vuex (`tarefas` module) is the single source of truth.
   *   - On non-200 responses we preserve the last-good cache (no reset).
   *
   * @param {boolean} forceUpdate - bypass TTL and force a fresh request
   */
  // eslint-disable-next-line max-statements
  async carregarAssignees(forceUpdate = false) {
    if (this.reqCarregar != null) {
      return this.reqCarregar
    }
    if (!forceUpdate) {
      if (
        !diffTimeMinutes(
          store.getters['tarefas/assigneesLastUpdate'],
          MINUTES_TIMEOUT_REQUESTS
        )
      ) {
        return store.getters['tarefas/assignees']
      }
    }

    if (store.getters['tarefas/assigneesLoading']) {
      return store.getters['tarefas/assignees']
    }

    store.dispatch('tarefas/setAssigneesLoading', true)

    this.reqCarregar = con.get(endpoint.assignees)
    const r = await this.reqCarregar

    if (r && r.status === 200) {
      store.dispatch(
        'tarefas/setAssignees',
        r.value || { professionals: [], clients: [] }
      )
    }

    store.dispatch('tarefas/setAssigneesLoading', false)

    this.reqCarregar = null

    return r || {}
  }

  /**
   * Fetches all active custom field definitions for the current office.
   * Returns fields sorted by display_order.
   * Frontend adapter: rewrites backend 'select' to 'options' (single source of truth).
   */
  async getCustomFields() {
    const r = await con.get(endpoint.customFields)
    if (r && r.status === 200 && Array.isArray(r.value)) {
      r.value = r.value.map((f) => ({ ...f, type: mapTypeBackToFront(f.type) }))
    }
    return r
  }

  /**
   * Creates a new custom field definition (gestor-only).
   * Frontend adapter: rewrites 'options' to backend 'select'.
   * Apenas inclui no body keys que têm valor — evita enviar `options: null`
   * e `displayOrder: undefined` que podem disparar 500 no reqparse type=list.
   * @param {object} params
   * @param {string} params.name - Field name
   * @param {string} params.type - Field type (text, number, date, options)
   * @param {Array} [params.options] - Required for options type
   * @param {number} [params.displayOrder] - Display order
   */
  createCustomField({ name, type, options, displayOrder } = {}) {
    const payload = { name: name, type: mapTypeFrontToBack(type) }
    if (Array.isArray(options) && options.length > 0) {
      payload.options = options
    }
    if (displayOrder != null) {
      payload.displayOrder = displayOrder
    }
    return con.post(endpoint.customFields, payload)
  }

  /**
   * Updates a custom field definition (gestor-only).
   * Frontend adapter: rewrites 'options' to backend 'select' on data.type when present.
   * Remove keys com valor null/undefined antes de enviar para evitar 500 no
   * reqparse (ex: `options: null` para tipos não-Opções).
   * @param {object} params
   * @param {string} params.id - Custom field ID
   * @param {object} params.data - Fields to update
   */
  updateCustomField({ id, data } = {}) {
    const payload = _buildCustomFieldUpdatePayload(data)
    return con.put(endpoint.customFieldById(id), payload)
  }

  /**
   * Soft-deletes a custom field definition (gestor-only).
   * @param {object} params
   * @param {string} params.id - Custom field ID
   */
  deleteCustomField({ id } = {}) {
    return con.delet(endpoint.customFieldById(id))
  }

  /**
   * Sets the value of a custom field for a specific task.
   * @param {object} params
   * @param {string} params.taskId - Task ID
   * @param {string} params.fieldId - Custom field ID
   * @param {*} params.value - Field value
   */
  setTaskCustomFieldValue({ taskId, fieldId, value } = {}) {
    return con.put(endpoint.taskCustomFieldValue(taskId, fieldId), { value: value })
  }

  // ============================================================
  // Templates (pm_task_template) — gestor-only mutations, qualquer
  // profissional pode listar/visualizar/instanciar.
  // ============================================================

  /**
   * Lists root templates for the current office (parent_id IS NULL).
   * Backend returns subtaskCount via a single bulk query — no N+1.
   */
  getTemplates() {
    return con.get(endpoint.templates)
  }

  /**
   * Returns a template root plus its flat subtree (nodes array).
   * @param {object} params
   * @param {string} params.id - template root UUID
   */
  getTemplate({ id } = {}) {
    return con.get(endpoint.templateById(id))
  }

  /**
   * Creates a template root (gestor-only — backend enforces).
   * @param {object} params
   * @param {object} params.data - camelCase payload (title required)
   */
  createTemplate({ data } = {}) {
    return con.post(endpoint.templates, data)
  }

  /**
   * Updates a template node (root or subtask) — partial body, only sent keys.
   * @param {object} params
   * @param {string} params.id - template node UUID
   * @param {object} params.data - camelCase partial payload
   */
  updateTemplate({ id, data } = {}) {
    return con.put(endpoint.templateById(id), data)
  }

  /**
   * Hard-deletes a template root. CASCADE removes the subtree at the DB level.
   * @param {object} params
   * @param {string} params.id - template root UUID
   */
  deleteTemplate({ id } = {}) {
    return con.delet(endpoint.templateById(id))
  }

  /**
   * Adds a subtask node to an existing template (gestor-only).
   * parent_id is sourced from the URL — payload values are ignored server-side.
   * @param {object} params
   * @param {string} params.templateId - parent template node UUID
   * @param {object} params.data - camelCase payload
   */
  addTemplateNode({ templateId, data } = {}) {
    return con.post(endpoint.templateNodes(templateId), data)
  }

  /**
   * Creates a real task tree from a template (any professional).
   * baseDate as ISO 'YYYY-MM-DD'; backend normalizes camelCase → snake_case.
   * @param {object} params
   * @param {string} params.templateId - template root UUID
   * @param {object} params.payload - { title, sprintId, folderId, clienteAppId,
   *   baseDate, responsavelPrincipal, participantes }
   */
  async instantiateTemplate({ templateId, payload } = {}) {
    const r = await con.post(
      endpoint.templateInstantiate(templateId),
      _serializeResponsavelPrincipal(payload || {})
    )

    // Instanciar template nao commita ADD_TASK, entao quem observa criacao de
    // tarefa (ex: historico do CRM) so fica sabendo por aqui.
    //
    // O backend devolve so { id, title } — sem cliente. Quem consome precisa do
    // vinculo, e o proprio caller acabou de manda-lo, entao vai daqui.
    if (r && r.status === 200 && r.value) {
      // O endpoint cria uma ARVORE de tarefas; hoje devolve so a raiz como objeto,
      // mas normalizamos para nao virar `{0:…, clienteAppId}` se passar a devolver
      // lista — o consumidor ja trata array.
      const criadas = Array.isArray(r.value) ? r.value : [r.value]
      GlobalBus.$emit(
        TAREFA.CREATED,
        criadas.map((t) => ({ ...t, clienteAppId: (payload || {}).clienteAppId }))
      )
    }

    return r
  }

  /**
   * Converts an existing task tree into a template (gestor-only).
   * @param {object} params
   * @param {string} params.taskId - real task UUID (root or subtask)
   * @param {string} [params.templateTitle] - optional override; backend falls
   *   back to task.title when omitted
   */
  saveTaskAsTemplate({ taskId, templateTitle } = {}) {
    const data = {}
    if (templateTitle !== undefined) data.templateTitle = templateTitle
    return con.post(endpoint.taskSaveAsTemplate(taskId), data)
  }

  /**
   * Reorders subtask nodes within the same parent (gestor-only).
   * @param {object} params
   * @param {string} params.rootId - template root UUID (used in the URL path)
   * @param {string} params.parentId - UUID of the parent whose children are being reordered
   * @param {string[]} params.orderedIds - child node UUIDs in the desired order
   */
  reorderTemplateNodes({ rootId, parentId, orderedIds } = {}) {
    return con.patch(endpoint.templateNodesReorder(rootId), { parentId, orderedIds })
  }

  // ============================================================
  // Trigger mappings (pm_task_template_trigger) — gestor-only
  // mutations, qualquer profissional pode listar/buscar mappings
  // ativos. Backend já filtra por id_escritorio e pelo INNER JOIN
  // com pm_task_template (mappings orfaos sao ocultados).
  // ============================================================

  /**
   * Lists all trigger mappings of the current office.
   * Optional `eventType` filters by the catalog key (ex.: 'lead.move_stage_to_won').
   * Backend response items already include `templateTitle` (INNER JOIN).
   * @param {object} [params]
   * @param {string|null} [params.eventType] - optional event-type filter
   */
  getTriggerMappings({ eventType = null } = {}) {
    if (eventType) {
      return con.get(endpoint.triggerMappingsByEvent(eventType))
    }
    return con.get(endpoint.triggerMappings)
  }

  /**
   * Returns only ACTIVE trigger mappings for a given event (used by the
   * trigger registry handler after a CRM drag-to-Won). Backend filters out
   * inactive mappings and orphan templates.
   * @param {object} params
   * @param {string} params.eventType - catalog key (TRIGGER_EVENTS.*)
   */
  getActiveTriggerMappingsByEvent({ eventType } = {}) {
    return con.get(endpoint.triggerMappingsByEvent(eventType))
  }

  /**
   * Creates a trigger mapping (gestor-only — backend enforces).
   * @param {object} params
   * @param {object} params.data - camelCase payload
   *   { idTemplate: string, eventType: string, active?: boolean }
   */
  createTriggerMapping({ data } = {}) {
    return con.post(endpoint.triggerMappings, data)
  }

  /**
   * Updates a trigger mapping (gestor-only). Partial body — only `active`
   * and `idTemplate` are accepted; `eventType` is immutable.
   * @param {object} params
   * @param {string} params.id - mapping UUID
   * @param {object} params.data - camelCase partial payload
   */
  updateTriggerMapping({ id, data } = {}) {
    return con.put(endpoint.triggerMappingById(id), data)
  }

  /**
   * Hard-deletes a trigger mapping (gestor-only). No CASCADE side-effects —
   * templates are untouched.
   * @param {object} params
   * @param {string} params.id - mapping UUID
   */
  deleteTriggerMapping({ id } = {}) {
    return con.delet(endpoint.triggerMappingById(id))
  }
}

export const apiTarefas = new ApiTarefas()
